import {
    type ImageOverlayMapObject,
    type ImageOverlaySource,
    ImageOverlaysLayer,
    type MapItemHandler,
} from "@corvus/corvus-map";
import { useEffect, useMemo, useRef, useState } from "react";

import {
    getFirebaseStorageDownloadUrl,
    getFirebaseStoragePublicUrl,
} from "../../corvusFirebase";
import { getMeasurementDetailPath } from "./measurementMosaic";
import type { ProjectedMeasurementImage } from "./useProjectedMeasurementImages";

interface MeasurementImageOverlaysProps {
    id: string;
    projectedImages: readonly ProjectedMeasurementImage[];
    organizationId?: string;
    locationId?: string;
    selectedMeasurementId: string | null;
    selectedIds: ReadonlySet<string>;
    onItemClick: MapItemHandler<ImageOverlayMapObject>;
}

const DELETED_SOURCE: ImageOverlaySource = { status: "deleted" };

export default function MeasurementImageOverlays({
    id,
    projectedImages,
    organizationId,
    locationId,
    selectedMeasurementId,
    selectedIds,
    onItemClick,
}: MeasurementImageOverlaysProps) {
    const [resolvedDetailUrlsByPath, setResolvedDetailUrlsByPath] = useState<
        ReadonlyMap<string, string>
    >(() => new Map());
    const detailImagePromisesRef = useRef(new Map<string, Promise<string>>());
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const imageOverlays = useMemo<readonly ImageOverlayMapObject[]>(
        () =>
            projectedImages.map(({ id: imageId, corners, thumbnailPath }) => ({
                id: imageId,
                type: "image-overlay",
                source:
                    thumbnailPath === null
                        ? DELETED_SOURCE
                        : {
                              status: "image",
                              imageUrl:
                                  getFirebaseStoragePublicUrl(thumbnailPath),
                          },
                corners,
                interactive: true,
            })),
        [projectedImages],
    );

    const selectedDetailPath = useMemo(() => {
        if (!organizationId || !locationId || selectedMeasurementId === null)
            return null;

        const selectedImage = projectedImages.find(
            ({ id: imageId, thumbnailPath }) =>
                imageId === selectedMeasurementId && thumbnailPath !== null,
        );
        if (!selectedImage) return null;

        return getMeasurementDetailPath(
            organizationId,
            locationId,
            selectedMeasurementId,
        );
    }, [locationId, organizationId, projectedImages, selectedMeasurementId]);

    useEffect(() => {
        if (
            selectedDetailPath === null ||
            resolvedDetailUrlsByPath.has(selectedDetailPath) ||
            detailImagePromisesRef.current.has(selectedDetailPath)
        ) {
            return;
        }

        const detailImagePromise = getFirebaseStorageDownloadUrl(
            selectedDetailPath,
        ).finally(() => {
            detailImagePromisesRef.current.delete(selectedDetailPath);
        });

        detailImagePromisesRef.current.set(
            selectedDetailPath,
            detailImagePromise,
        );
        void detailImagePromise
            .then((detailImageUrl) => {
                if (!mountedRef.current) return;

                setResolvedDetailUrlsByPath((current) => {
                    const next = new Map(current);
                    next.set(selectedDetailPath, detailImageUrl);
                    return next;
                });
            })
            .catch(() => {
                // A missing detail image leaves the successfully loaded base image visible.
            });
    }, [resolvedDetailUrlsByPath, selectedDetailPath]);

    const items = useMemo(
        () =>
            imageOverlays.map((imageOverlay) => {
                const detailImageUrl =
                    imageOverlay.id === selectedMeasurementId &&
                    selectedDetailPath !== null
                        ? resolvedDetailUrlsByPath.get(selectedDetailPath)
                        : undefined;

                return detailImageUrl === undefined
                    ? imageOverlay
                    : { ...imageOverlay, detailImageUrl };
            }),
        [
            imageOverlays,
            resolvedDetailUrlsByPath,
            selectedDetailPath,
            selectedMeasurementId,
        ],
    );

    return (
        <ImageOverlaysLayer
            id={id}
            items={items}
            selectedIds={selectedIds}
            onItemClick={onItemClick}
        />
    );
}
